import { Achievement_Types_Enum, AchievementFragment, UserAchievementFragment } from '@/gql/generated/graphql'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { DetailText, BodyText, CheckIcon } from 'design-configuration/components'
import type { SetProgress } from '@artblocks/shared-types'
import { motion } from 'motion/react'
import { AchievementDetailsModal } from '../achievement-details-modal'
import { useState } from 'react'
import { Button } from '@components/button'
import { LoadingSpinner } from '@components/loading-spinner'
import { useIsProfileOwner } from '../../../components/profile-context'
import { cn } from '@/lib/utils'
import { claimAchievement } from '../../_actions/claim-achievement'
import { formatDate, getRelativeTimeString } from '@utils/common'
import { getSetImageUrl } from '@utils/sets'

export enum AchievementGridView {
  Table = 'table',
  Grid = 'grid'
}

// Special type to identify header items
export type AchievementTableHeaderItem = {
  isHeader: true
}

export const AchievementTableHeader = ({ achieved }: { achieved: boolean }): React.ReactElement => {
  return (
    <div className="rounded-xs border-outline-variant pr-sm py-xs bg-surface hidden w-full grid-cols-5 lg:grid">
      <div className="px-md col-span-1 flex items-center justify-start">
        <DetailText className="text-on-surface-variant font-medium uppercase">Achievement</DetailText>
      </div>
      <div className="col-span-2 flex items-center justify-start"></div>
      <div className="col-span-1 flex items-center justify-end">
        <DetailText className="text-on-surface-variant font-medium uppercase">Type</DetailText>
      </div>
      <div className="col-span-1 flex items-center justify-end">
        <DetailText className="text-on-surface-variant font-medium uppercase">
          {achieved ? 'Certified' : 'Progress'}
        </DetailText>
      </div>
    </div>
  )
}

export const ClaimAchievementButton = ({
  type,
  userAchievement,
  onClaimSuccess,
  className
}: {
  type: 'primary' | 'outline'
  userAchievement: UserAchievementFragment
  onClaimSuccess: () => void
  className?: string
}): React.ReactElement | null => {
  const [isClaiming, setIsClaiming] = useState(false)
  const isProfileOwner = useIsProfileOwner()

  const handleClaimClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!userAchievement) return

    try {
      setIsClaiming(true)
      await claimAchievement(userAchievement.user_profile_id, userAchievement.achievement.id)
      onClaimSuccess()
    } catch (error) {
      console.error('Error claiming achievement:', error)
    } finally {
      setIsClaiming(false)
    }
  }

  if (!isProfileOwner || !userAchievement || userAchievement.claimed) {
    return null
  }

  return (
    <Button
      colorTheme={type}
      branded={type === 'primary'}
      onClick={handleClaimClick}
      disabled={isClaiming}
      className={cn('px-xs py-xs', className)}
      iconLeft={type === 'outline' && !isClaiming ? <CheckIcon className="w-sm h-sm" /> : undefined}
    >
      {isClaiming ? <LoadingSpinner className="w-sm h-sm" light={type === 'primary'} /> : 'Claim'}
    </Button>
  )
}

export const AchievementGridItem = ({
  achievement,
  userAchievement,
  setProgress,
  onRefetch
}: {
  achievement: AchievementFragment
  userAchievement?: UserAchievementFragment
  setProgress?: SetProgress
  onRefetch?: () => Promise<void> | void
}): React.ReactElement => {
  const [modalOpen, setModalOpen] = useState(false)
  const [newlyClaimed, setNewlyClaimed] = useState(false)

  const handleModalDismiss = async () => {
    if ((newlyClaimed || !userAchievement?.claimed) && onRefetch) {
      await onRefetch()
    }
    setModalOpen(false)
    setNewlyClaimed(false)
  }
  const achieved = !!userAchievement

  const imageUrl =
    userAchievement?.token?.media_url ?? achievement.image?.url ?? getSetImageUrl(achievement.set ?? undefined)

  const progress = setProgress?.progress ?? (achieved ? 100 : 0)
  return (
    <>
      <div
        className={cn(
          'hover:shadow-artwork relative flex cursor-pointer flex-col items-center rounded-full p-[2px] transition-shadow',
          achievement.type === Achievement_Types_Enum.Historical
            ? 'bg-gradient-chromie'
            : achieved
              ? 'bg-primary'
              : 'bg-outline-variant'
        )}
        onClick={() => setModalOpen(true)}
        role="button"
      >
        <div className="bg-surface relative aspect-square w-full overflow-hidden rounded-full">
          {imageUrl ? (
            <ImageWithCustomLoader
              src={imageUrl}
              alt={achievement.name}
              fill
              className={cn('object-cover', !achieved && 'opacity-50 grayscale')}
            />
          ) : (
            <div
              className={cn(
                'bg-surface flex aspect-square w-full items-center justify-center bg-[linear-gradient(248deg,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.00)_33.51%,rgba(0,0,0,0.05)_99.56%)]',
                !achieved && 'grayscale'
              )}
            ></div>
          )}
          <div
            className={cn(
              'px-xs py-xs bg-surface absolute inset-x-0 top-1/2 flex -translate-y-1/2 transform flex-col items-center justify-center border-b-2 border-t-2 text-center',
              achieved ? 'border-primary' : 'border-outline-variant'
            )}
          >
            <DetailText className="font-medium uppercase">{achievement.name}</DetailText>
            {!(achieved && userAchievement?.claimed) ? (
              <div className="mt-2xs px-xs flex w-full flex-col items-center justify-center">
                {userAchievement && onRefetch ? (
                  <ClaimAchievementButton
                    type="primary"
                    userAchievement={userAchievement}
                    onClaimSuccess={() => {
                      setNewlyClaimed(true)
                      setModalOpen(true)
                    }}
                    className="mt-2xs w-full"
                  />
                ) : (
                  <>
                    <div className="bg-surface-container h-[4px] w-full overflow-hidden rounded-full">
                      <motion.div
                        className="bg-primary h-full"
                        initial={{
                          width: '0%'
                        }}
                        animate={{
                          width: `${progress}%`
                        }}
                        transition={{
                          duration: 0.5,
                          ease: 'easeInOut'
                        }}
                      />
                    </div>
                    <DetailText className="text-on-surface-variant mt-2xs text-center">
                      {progress
                        ? `${progress}% complete`
                        : achievement.type === Achievement_Types_Enum.Historical
                          ? 'Honor'
                          : 'Not started'}
                    </DetailText>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <AchievementDetailsModal
        show={modalOpen}
        onDismiss={handleModalDismiss}
        achievement={achievement}
        userAchievement={userAchievement}
        token={userAchievement?.token ?? undefined}
        newlyClaimed={newlyClaimed}
        key={achievement.id}
        onClaimSuccess={async () => {
          if (onRefetch) {
            await onRefetch()
          }
        }}
      />
    </>
  )
}

export const achievementTypeToDisplayString = (type: Achievement_Types_Enum): string => {
  if (!type) return 'Unknown'

  switch (type.toLowerCase()) {
    case Achievement_Types_Enum.Milestone:
      return 'Milestone'
    case Achievement_Types_Enum.SetCompletion:
      return 'Set'
    case Achievement_Types_Enum.Historical:
      return 'Honor'
    default:
      return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
  }
}

export const AchievementTableItem = ({
  achievement,
  userAchievement,
  setProgress,
  onRefetch
}: {
  achievement: AchievementFragment
  userAchievement?: UserAchievementFragment
  setProgress?: SetProgress
  onRefetch?: () => Promise<void> | void
}): React.ReactElement => {
  const [modalOpen, setModalOpen] = useState(false)
  const [newlyClaimed, setNewlyClaimed] = useState(false)

  const handleModalDismiss = async () => {
    if (newlyClaimed && onRefetch) {
      await onRefetch()
    }
    setModalOpen(false)
    setNewlyClaimed(false)
  }

  const imageUrl =
    userAchievement?.token?.media_url ?? achievement.image?.url ?? getSetImageUrl(achievement.set ?? undefined) ?? ''

  const achieved = !!userAchievement
  const achievedGrid = achieved && userAchievement?.claimed

  return (
    <>
      <div
        className="rounded-xs border-outline-variant hover:bg-surface-container flex w-full cursor-pointer border-t transition-colors"
        onClick={() => setModalOpen(true)}
        role="button"
      >
        <div className="p-md">
          {imageUrl ? (
            <div
              className={cn(
                'relative h-full min-h-[100px] w-[100px] overflow-hidden rounded-full p-[2px]',
                achievement.type === Achievement_Types_Enum.Historical
                  ? 'bg-gradient-chromie'
                  : achieved
                    ? 'bg-primary'
                    : 'bg-outline-variant'
              )}
            >
              <div className="bg-surface h-full w-full overflow-hidden rounded-full">
                <div
                  className={cn(
                    'relative h-full w-full overflow-hidden rounded-full',
                    !achieved && 'opacity-50 grayscale'
                  )}
                >
                  <ImageWithCustomLoader src={imageUrl} alt={achievement.name} fill className="object-cover" />
                </div>
              </div>
            </div>
          ) : (
            <div
              className={cn(
                'flex h-full min-h-[100px] w-[100px] items-center justify-center rounded-full p-[2px]',
                achievement.type === Achievement_Types_Enum.Historical
                  ? 'bg-gradient-chromie'
                  : achieved
                    ? 'bg-primary'
                    : 'bg-outline-variant'
              )}
            >
              <div className="bg-surface flex h-full w-full items-center justify-center rounded-full bg-[linear-gradient(248deg,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.00)_33.51%,rgba(0,0,0,0.05)_99.56%)]">
                <div
                  className={cn(
                    'flex h-full w-full items-center justify-center rounded-full',
                    !achieved && 'opacity-50 grayscale'
                  )}
                ></div>
              </div>
            </div>
          )}
        </div>
        <div className="px-sm py-sm lg:py-md gap-xs grid w-full grid-cols-2 lg:grid-cols-5">
          <div className="gap-y-2xs col-span-2 flex flex-col items-start justify-center p-0 lg:col-span-3">
            <BodyText className="font-medium">{achievement.name}</BodyText>
            <BodyText>{achievement.description}</BodyText>
          </div>
          <div className="col-span-1 hidden items-center justify-end lg:flex">
            <div className="border-on-surface-variant px-sm py-2xs w-fit rounded-full border font-medium uppercase">
              <DetailText className="text-on-surface-variant">
                {achievementTypeToDisplayString(achievement.type ?? Achievement_Types_Enum.Milestone)}
              </DetailText>
            </div>
          </div>
          {achievedGrid ? (
            <div className="col-span-1 flex w-full flex-col items-start justify-center lg:items-end">
              <BodyText className="hidden lg:block">{getRelativeTimeString(userAchievement?.earned_at ?? '')}</BodyText>
              <DetailText className="text-on-surface-variant">
                <span className="lg:hidden">Certified on </span>
                {formatDate(new Date(userAchievement?.earned_at ?? ''), {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </DetailText>
            </div>
          ) : (
            <div className="col-span-1 flex w-full items-center justify-start lg:justify-end">
              {userAchievement && onRefetch ? (
                <ClaimAchievementButton
                  type="primary"
                  userAchievement={userAchievement}
                  onClaimSuccess={() => {
                    setNewlyClaimed(true)
                    setModalOpen(true)
                  }}
                  className="px-sm py-xs gap-x-xs"
                />
              ) : setProgress?.progress ? (
                <motion.div layout className="gap-y-2xs flex w-[125px] flex-col">
                  <div className="bg-surface-container h-[4px] w-full overflow-hidden rounded-full">
                    <motion.div
                      className={`bg-primary h-full`}
                      initial={{
                        width: '0%'
                      }}
                      animate={{
                        width: `${setProgress.progress}%`
                      }}
                      transition={{
                        duration: 0.5,
                        ease: 'easeInOut'
                      }}
                    />
                  </div>
                  <DetailText className="text-on-surface-variant lg:text-right">
                    {setProgress.progress}% complete
                  </DetailText>
                </motion.div>
              ) : (
                <DetailText className="text-on-surface-variant font-medium">{'--'}</DetailText>
              )}
            </div>
          )}
        </div>
      </div>

      <AchievementDetailsModal
        key={achievement.id}
        show={modalOpen}
        onDismiss={handleModalDismiss}
        achievement={achievement}
        userAchievement={userAchievement}
        token={userAchievement?.token ?? undefined}
        newlyClaimed={newlyClaimed}
        onClaimSuccess={async () => {
          if (onRefetch) {
            await onRefetch()
          }
        }}
      />
    </>
  )
}
